import qs from 'qs';

import { TERMINAL_OPPORTUNITY_STATUSES } from '~/consts/priceParrot';

import {
  BulkCreateSfOpportunitiesBody,
  BulkCreateSfOpportunitiesResponse,
  ChainLpcLppSupplierBreakdownApiResponse,
  ChainLpcLppSupplierChainsListApiResponse,
  ChainLpcLppSupplierDailyTrendApiResponse,
  ChainLpcLppSupplierSortMode,
  ChainWeightingMode,
  CompetitorHotelOpportunities,
  CompetitorProperty,
  CompetitorPropertyHistoryEntry,
  CompetitorPropertyOption,
  CompetitorPropertySnapshot,
  CompetitorSourceName,
  ControlCentreProperty,
  CreateSfOpportunityBody,
  CreateSfOpportunityResponse,
  PaginatedPriceParrotQueryParams,
  PaginatedPriceParrotResponse,
  ParityTrendChainCompetitorsApiResponse,
  ParityTrendChainPropertiesApiResponse,
  ParityTrendChainRangeSummaryApiResponse,
  ParityTrendChainSeriesApiResponse,
  ParityTrendChainsListApiResponse,
  ParityTrendRangeSummaryApiResponse,
  ParityTrendSeriesApiResponse,
  PriceSummaryJob,
  PriceSummaryJobBody,
  PriceSummaryJobQueryParams,
  Property,
  RawControlCentreProperty,
  RawControlCentreResponse,
  ScheduledPriceSummaryJob,
  ScheduledPriceSummaryJobBody,
  ScheduledPriceSummaryJobRun,
  ScheduledPriceSummaryJobVariation,
  ScheduledPriceSummaryJobVariationBody,
  TriggerNotificationBody,
  TriggerNotificationResponse,
} from '~/types/services/priceParrot';

import { json_headers, request } from './common';

function basePath() {
  return window.configs.API_HOST + '/api/price-parrot';
}

function getPriceSummaryJobs(
  queryParams: PriceSummaryJobQueryParams,
): Promise<PaginatedPriceParrotResponse<PriceSummaryJob>> {
  return request(`${basePath()}/price-summary/jobs?${qs.stringify(queryParams)}`, {
    method: 'GET',
    headers: json_headers,
  });
}

function postPriceSummaryJob(priceSummaryBody: PriceSummaryJobBody): Promise<{ result: { job_id: string } }> {
  return request(`${basePath()}/price-summary/jobs`, {
    headers: json_headers,
    method: 'POST',
    body: JSON.stringify(priceSummaryBody),
  });
}

function downloadPriceSummaryJob(jobId: string): Promise<Response> {
  return request(`${basePath()}/price-summary/jobs/${jobId}/download`, {
    returnResponse: true,
    method: 'GET',
  });
}

function getEventSourceURL(): string {
  return `${basePath()}/price-summary/jobs/status/stream`;
}

function cancelPriceSummaryJob(jobId: string): Promise<void> {
  return request(`${basePath()}/price-summary/jobs/${jobId}/cancel`, {
    method: 'POST',
    headers: json_headers,
  });
}

function getScheduledPriceSummaryJobs(
  queryParams: PaginatedPriceParrotQueryParams,
): Promise<PaginatedPriceParrotResponse<ScheduledPriceSummaryJob>> {
  return request(`${basePath()}/scheduled-price-summary/jobs?${qs.stringify(queryParams)}`, {
    method: 'GET',
    headers: json_headers,
  });
}

function postScheduledPriceSummaryJob(body: ScheduledPriceSummaryJobBody): Promise<{ job: ScheduledPriceSummaryJob }> {
  return request(`${basePath()}/scheduled-price-summary/jobs`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

function patchScheduledPriceSummaryJob(
  jobId: number,
  body: Pick<ScheduledPriceSummaryJob, 'status' | 'scheduleInterval'>,
): Promise<{ job: ScheduledPriceSummaryJob }> {
  return request(`${basePath()}/scheduled-price-summary/jobs/${jobId}`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

function getScheduledPriceSummaryJob(jobId: string): Promise<{ data: ScheduledPriceSummaryJob }> {
  return request(`${basePath()}/scheduled-price-summary/jobs/${jobId}`, { method: 'GET', headers: json_headers });
}

function postScheduledPriceSummaryJobVariation(
  jobId: number,
  body: ScheduledPriceSummaryJobVariationBody,
): Promise<{ data: ScheduledPriceSummaryJobVariation }> {
  return request(`${basePath()}/scheduled-price-summary/jobs/${jobId}/variations`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

function deleteScheduledPriceSummaryJobVariation(jobId: number, variationId: number): Promise<void> {
  return request(`${basePath()}/scheduled-price-summary/jobs/${jobId}/variations/${variationId}`, {
    method: 'DELETE',
    headers: json_headers,
  });
}

function getScheduledPriceSummaryJobRuns(jobId: string): Promise<{ data: Array<ScheduledPriceSummaryJobRun> }> {
  return request(`${basePath()}/scheduled-price-summary/jobs/${jobId}/runs`, {
    method: 'GET',
    headers: json_headers,
  });
}

function downloadScheduledPriceSummaryJobRun(jobId: number, runId: number): Promise<Response> {
  return request(`${basePath()}/scheduled-price-summary/jobs/${jobId}/runs/${runId}/download`, {
    returnResponse: true,
    method: 'GET',
  });
}

function searchProperty(search: string): Promise<{ data: Array<Property> }> {
  return request(`${basePath()}/property/search?${qs.stringify({ search })}`, {
    method: 'GET',
    headers: json_headers,
  });
}

function vendorParityBasePath() {
  return window.configs.API_HOST + '/api/vendor/parity';
}

function vendorBasePath() {
  return window.configs.API_HOST + '/api/vendor';
}

function mapRawProperty(raw: RawControlCentreProperty): ControlCentreProperty {
  return {
    propertyId: raw.property_id ?? raw.offer_bk,
    propertyName: raw.property_name ?? raw.offer_bk,
    offerBk: raw.offer_bk,
    vendorBk: raw.vendor_bk ?? null,
    offerCountryCode: raw.offer_country_code ?? null,
    offerCountryName: raw.offer_country_name ?? null,
    impressionsOverpriced: raw.impressions_overpriced_l30d ?? 0,
    pctImpressionsOverpriced: (raw.pct_impressions_overpriced_l30d ?? 0) * 100,
    pctOverpriced: (raw.pct_overpriced_l30d ?? 0) * 100,
    mpv7d: raw.mpv_l7d ?? 0,
    mpv30d: raw.mpv_l30d ?? 0,
    connectivityActionStatus: raw.sf_opportunity_status ?? null,
    lastActivityDate: raw.sf_last_activity ?? null,
    closedDate: raw.sf_closed_date ?? null,
    createdDate: raw.sf_created_date ?? null,
    vnsDays7d: raw.vns_days ?? null,
    openOpp: raw.sf_opportunity_status != null && !TERMINAL_OPPORTUNITY_STATUSES.has(raw.sf_opportunity_status),
    bedbankLpcPct: raw.bedbank_lpc_ratio != null ? raw.bedbank_lpc_ratio * 100 : null,
    yoyDeclinePct: raw.yoy_ttv_change != null ? raw.yoy_ttv_change * 100 : null,
    tier: raw.deal_tier != null ? parseInt(raw.deal_tier.match(/\d+$/)?.[0] ?? '', 10) || null : null,
    propertyScore7d: raw.property_parity_score_7d ?? null,
    propertyScore30d: raw.property_parity_score_30d ?? null,
  };
}

async function getControlCentreProperties(signal?: AbortSignal): Promise<Array<ControlCentreProperty>> {
  const response: RawControlCentreResponse = await request(`${vendorParityBasePath()}/control-centre`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });

  return response.data.map(mapRawProperty);
}

function createConnectivityAction(body: CreateSfOpportunityBody): Promise<CreateSfOpportunityResponse> {
  return request(`${vendorParityBasePath()}/create-sf-opportunity`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ property_id: body.propertyId }),
  });
}

function triggerExtranetNotification(body: TriggerNotificationBody): Promise<TriggerNotificationResponse> {
  return request(`${vendorParityBasePath()}/trigger-extranet-notification`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ property_id: body.propertyId }),
  });
}

function bulkCreateSfOpportunities(body: BulkCreateSfOpportunitiesBody): Promise<BulkCreateSfOpportunitiesResponse> {
  return request(`${vendorParityBasePath()}/bulk-create-sf-opportunities`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

function getParityTrendSeries(
  offerBk: string,
  params: { date_from: string; date_to: string },
  signal?: AbortSignal,
): Promise<ParityTrendSeriesApiResponse> {
  const query = qs.stringify(params);
  return request(`${vendorParityBasePath()}/trend/offers/${encodeURIComponent(offerBk)}/series?${query}`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });
}

function getParityTrendRangeSummary(
  offerBk: string,
  params: { date_from: string; date_to: string },
  signal?: AbortSignal,
): Promise<ParityTrendRangeSummaryApiResponse> {
  const query = qs.stringify(params);
  return request(`${vendorParityBasePath()}/trend/offers/${encodeURIComponent(offerBk)}/summary?${query}`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });
}

function getParityTrendChains(
  params: { date_from: string; date_to: string },
  signal?: AbortSignal,
): Promise<ParityTrendChainsListApiResponse> {
  const query = qs.stringify(params);
  return request(`${vendorParityBasePath()}/trend/chains?${query}`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });
}

function getParityTrendChainSeries(
  chainName: string,
  params: { date_from: string; date_to: string; weighting?: ChainWeightingMode; continent?: string },
  signal?: AbortSignal,
): Promise<ParityTrendChainSeriesApiResponse> {
  const query = qs.stringify(params);
  return request(`${vendorParityBasePath()}/trend/chains/${encodeURIComponent(chainName)}/series?${query}`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });
}

function getParityTrendChainRangeSummary(
  chainName: string,
  params: { date_from: string; date_to: string; weighting?: ChainWeightingMode; continent?: string },
  signal?: AbortSignal,
): Promise<ParityTrendChainRangeSummaryApiResponse> {
  const query = qs.stringify(params);
  return request(`${vendorParityBasePath()}/trend/chains/${encodeURIComponent(chainName)}/summary?${query}`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });
}

function getParityTrendChainProperties(
  chainName: string,
  params: {
    date_from: string;
    date_to: string;
    limit?: number;
    continent?: string;
    weighting?: ChainWeightingMode;
  },
  signal?: AbortSignal,
): Promise<ParityTrendChainPropertiesApiResponse> {
  const query = qs.stringify(params);
  return request(`${vendorParityBasePath()}/trend/chains/${encodeURIComponent(chainName)}/properties?${query}`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });
}

function getParityTrendChainCompetitors(
  chainName: string,
  params: { date_from: string; date_to: string; continent?: string },
  signal?: AbortSignal,
): Promise<ParityTrendChainCompetitorsApiResponse> {
  const query = qs.stringify(params);
  return request(`${vendorParityBasePath()}/trend/chains/${encodeURIComponent(chainName)}/competitors?${query}`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });
}

function getParityTrendChainLpcLppSuppliers(
  chainName: string,
  params: {
    date_from: string;
    date_to: string;
    offer_bk?: string;
    sort?: ChainLpcLppSupplierSortMode;
  },
  signal?: AbortSignal,
): Promise<ChainLpcLppSupplierBreakdownApiResponse> {
  const query = qs.stringify(params);
  return request(`${vendorBasePath()}/chains/${encodeURIComponent(chainName)}/lpc-lpp-breakdown?${query}`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });
}

function getChainLpcLppSupplierChains(
  params: { date_from: string; date_to: string },
  signal?: AbortSignal,
): Promise<ChainLpcLppSupplierChainsListApiResponse> {
  const query = qs.stringify(params);
  return request(`${vendorBasePath()}/chains/lpc-lpp-breakdown/chains?${query}`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });
}

function getParityTrendChainLpcLppDailyTrend(
  chainName: string,
  params: {
    date_from: string;
    date_to: string;
    /** Omit to get a chain-wide rollup instead of one property's trend. */
    offer_bk?: string;
  },
  signal?: AbortSignal,
): Promise<ChainLpcLppSupplierDailyTrendApiResponse> {
  const query = qs.stringify(params);
  return request(`${vendorBasePath()}/chains/${encodeURIComponent(chainName)}/lpc-lpp-breakdown/trend?${query}`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });
}

function competitorPropertyPath(sourceName: CompetitorSourceName, externalPropertyId: string) {
  return `${basePath()}/competitor-offers/properties/${encodeURIComponent(sourceName)}/${encodeURIComponent(
    externalPropertyId,
  )}`;
}

// from and to are YYYY-MM-DD report dates, both included.
function getCompetitorHotelOpportunities(
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<{ data: Array<CompetitorHotelOpportunities> }> {
  return request(`${basePath()}/competitor-offers/opportunities?${qs.stringify({ from, to })}`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });
}

function getCompetitorProperties(signal?: AbortSignal): Promise<{ data: Array<CompetitorProperty> }> {
  return request(`${basePath()}/competitor-offers/properties`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });
}

function getCompetitorPropertyOptions(
  sourceName: CompetitorSourceName,
  externalPropertyId: string,
  signal?: AbortSignal,
): Promise<{ data: Array<CompetitorPropertyOption> }> {
  return request(`${competitorPropertyPath(sourceName, externalPropertyId)}/options`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });
}

function getCompetitorPropertyHistory(
  sourceName: CompetitorSourceName,
  externalPropertyId: string,
  signal?: AbortSignal,
): Promise<{ data: Array<CompetitorPropertyHistoryEntry> }> {
  return request(`${competitorPropertyPath(sourceName, externalPropertyId)}/history`, {
    method: 'GET',
    headers: json_headers,
    signal,
  });
}

function getCompetitorPropertySnapshot(
  sourceName: CompetitorSourceName,
  externalPropertyId: string,
  collectionId: string,
  signal?: AbortSignal,
): Promise<{ data: CompetitorPropertySnapshot }> {
  return request(
    `${competitorPropertyPath(sourceName, externalPropertyId)}/collections/${encodeURIComponent(collectionId)}`,
    {
      method: 'GET',
      headers: json_headers,
      signal,
    },
  );
}

export default {
  getPriceSummaryJobs,
  postPriceSummaryJob,
  downloadPriceSummaryJob,
  getEventSourceURL,
  cancelPriceSummaryJob,
  getScheduledPriceSummaryJobs,
  postScheduledPriceSummaryJob,
  patchScheduledPriceSummaryJob,
  getScheduledPriceSummaryJob,
  postScheduledPriceSummaryJobVariation,
  deleteScheduledPriceSummaryJobVariation,
  getScheduledPriceSummaryJobRuns,
  downloadScheduledPriceSummaryJobRun,
  searchProperty,
  getControlCentreProperties,
  createConnectivityAction,
  triggerExtranetNotification,
  bulkCreateSfOpportunities,
  getParityTrendSeries,
  getParityTrendRangeSummary,
  getParityTrendChains,
  getParityTrendChainSeries,
  getParityTrendChainRangeSummary,
  getParityTrendChainProperties,
  getParityTrendChainCompetitors,
  getParityTrendChainLpcLppSuppliers,
  getParityTrendChainLpcLppDailyTrend,
  getChainLpcLppSupplierChains,
  getCompetitorProperties,
  getCompetitorPropertyOptions,
  getCompetitorPropertyHistory,
  getCompetitorPropertySnapshot,
  getCompetitorHotelOpportunities,
};
